import { useState, useEffect, useRef } from 'react';
import { Search } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { createContact, getMatters, addMatterCoClient, checkContactDuplicates } from '../api/client';
import type { Matter, DuplicateMatch } from '../api/client';
import { useIsMobile } from '../hooks/useIsMobile';
import DuplicateContactWarning from './DuplicateContactWarning';
import Dialog from './Dialog';

function useDebounce(value: string, delay: number) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

const inputStyle = {
  width: '100%',
  padding: '0.4rem 0.6rem',
  borderRadius: 6,
  border: '1px solid var(--border)',
  fontSize: '0.85rem',
  boxSizing: 'border-box' as const,
};

const labelStyle = {
  display: 'block',
  fontSize: '0.75rem',
  color: 'var(--text-secondary)',
  marginBottom: '0.2rem',
  fontWeight: 600 as const,
};

interface Props {
  onClose: () => void;
}

export default function CreateContactModal({ onClose }: Props) {
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    middle_name: '',
    name_prefix: '',
    name_suffix: '',
    employer: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    source: '',
  });

  // Duplicate detection state
  const [duplicates, setDuplicates] = useState<DuplicateMatch[]>([]);
  const [dupLoading, setDupLoading] = useState(false);
  const [dupDismissed, setDupDismissed] = useState(false);
  const debouncedFirstName = useDebounce(form.first_name, 500);
  const debouncedLastName = useDebounce(form.last_name, 500);
  const debouncedEmail = useDebounce(form.email, 500);
  const debouncedPhone = useDebounce(form.phone, 500);

  // Reset dismissed state when name changes
  useEffect(() => { setDupDismissed(false); }, [debouncedFirstName, debouncedLastName]);

  // Debounced duplicate check
  useEffect(() => {
    if (debouncedFirstName.trim().length < 2 || debouncedLastName.trim().length < 2) {
      setDuplicates([]);
      return;
    }
    let cancelled = false;
    setDupLoading(true);
    checkContactDuplicates({
      first_name: debouncedFirstName.trim(),
      last_name: debouncedLastName.trim(),
      email: debouncedEmail.trim() || undefined,
      phone: debouncedPhone.trim() || undefined,
    })
      .then((res) => { if (!cancelled) setDuplicates(res.matches); })
      .catch(() => { if (!cancelled) setDuplicates([]); })
      .finally(() => { if (!cancelled) setDupLoading(false); });
    return () => { cancelled = true; };
  }, [debouncedFirstName, debouncedLastName, debouncedEmail, debouncedPhone]);

  // Matter association state
  const [matterSearch, setMatterSearch] = useState('');
  const [selectedMatter, setSelectedMatter] = useState<Pick<Matter, 'id' | 'title' | 'contact_name' | 'case_number'> | null>(null);
  const [showMatterDropdown, setShowMatterDropdown] = useState(false);
  const matterDropdownRef = useRef<HTMLDivElement>(null);
  const debouncedMatterSearch = useDebounce(matterSearch, 300);

  const { data: matterResultsData } = useQuery({
    queryKey: ['matters', 'search', debouncedMatterSearch],
    queryFn: () => getMatters({ search: debouncedMatterSearch, limit: 10 }),
    enabled: debouncedMatterSearch.length >= 2,
  });
  const matterResults = matterResultsData?.items ?? [];

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (matterDropdownRef.current && !matterDropdownRef.current.contains(e.target as Node)) {
        setShowMatterDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const mutation = useMutation({
    mutationFn: async () => {
      const data = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, v.trim() || null])
      );
      data.first_name = form.first_name.trim();
      data.last_name = form.last_name.trim();
      const contact = await createContact(data);
      if (selectedMatter) {
        await addMatterCoClient(selectedMatter.id, { contact_id: contact.id });
      }
      return contact;
    },
    onSuccess: (contact) => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      if (selectedMatter) {
        queryClient.invalidateQueries({ queryKey: ['matterCoClients', selectedMatter.id] });
      }
      onClose();
      navigate(`/contacts/${contact.id}`);
    },
  });

  const [attempted, setAttempted] = useState(false);

  const set = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const canSubmit = form.first_name.trim() && form.last_name.trim();

  const reqStyle = (val: string) => ({
    ...inputStyle,
    ...(attempted && !val.trim() ? { borderColor: '#ef4444', boxShadow: '0 0 0 1px #ef4444' } : {}),
  });

  return (
    <Dialog onClose={onClose} ariaLabel="New Contact">
      <div
        style={{
          background: 'var(--card)',
          borderRadius: 'var(--radius-md)',
          padding: '1.5rem',
          maxWidth: 480,
          width: '92vw',
          maxHeight: '90vh',
          overflow: 'auto',
        }}
      >
        <h2 style={{ margin: '0 0 1rem', fontSize: '1.1rem' }}>New Contact</h2>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.75rem' }}>
          <div>
            <label style={labelStyle}>First Name *</label>
            <input style={reqStyle(form.first_name)} value={form.first_name} onChange={set('first_name')} autoFocus />
            {attempted && !form.first_name.trim() && <span style={{ color: '#ef4444', fontSize: '0.72rem' }}>Required</span>}
          </div>
          <div>
            <label style={labelStyle}>Last Name *</label>
            <input style={reqStyle(form.last_name)} value={form.last_name} onChange={set('last_name')} />
            {attempted && !form.last_name.trim() && <span style={{ color: '#ef4444', fontSize: '0.72rem' }}>Required</span>}
          </div>
          <div>
            <label style={labelStyle}>Prefix</label>
            <input style={inputStyle} value={form.name_prefix} onChange={set('name_prefix')} placeholder="Mr., Mrs., Dr." />
          </div>
          <div>
            <label style={labelStyle}>Middle Name</label>
            <input style={inputStyle} value={form.middle_name} onChange={set('middle_name')} />
          </div>
          <div>
            <label style={labelStyle}>Suffix</label>
            <input style={inputStyle} value={form.name_suffix} onChange={set('name_suffix')} placeholder="Jr., Sr., III" />
          </div>
          <div>
            <label style={labelStyle}>Email</label>
            <input style={inputStyle} type="email" value={form.email} onChange={set('email')} />
          </div>
          <div>
            <label style={labelStyle}>Phone</label>
            <input style={inputStyle} value={form.phone} onChange={set('phone')} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Address</label>
            <input style={inputStyle} value={form.address} onChange={set('address')} />
          </div>
          <div>
            <label style={labelStyle}>City</label>
            <input style={inputStyle} value={form.city} onChange={set('city')} />
          </div>
          <div>
            <label style={labelStyle}>State</label>
            <input style={inputStyle} value={form.state} onChange={set('state')} />
          </div>
          <div>
            <label style={labelStyle}>Zip Code</label>
            <input style={inputStyle} value={form.zip_code} onChange={set('zip_code')} />
          </div>
          <div>
            <label style={labelStyle}>Source</label>
            <input style={inputStyle} value={form.source} onChange={set('source')} placeholder="e.g. Website, Referral" />
          </div>
          <div>
            <label style={labelStyle}>Employer</label>
            <input style={inputStyle} value={form.employer} onChange={set('employer')} />
          </div>
        </div>

        {/* Matter association (optional) */}
        <div style={{ marginTop: '0.75rem', position: 'relative' }} ref={matterDropdownRef}>
          <label style={labelStyle}>Associate with Matter (optional)</label>
          {selectedMatter ? (
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.4rem',
                background: 'var(--info-light)',
                border: '1px solid #c5c9ff',
                borderRadius: 16,
                padding: '0.3rem 0.5rem 0.3rem 0.7rem',
                fontSize: '0.82rem',
                color: 'var(--text)',
              }}
            >
              <span>{selectedMatter.title}</span>
              {selectedMatter.case_number && (
                <span style={{ color: 'var(--text-muted)' }}>({selectedMatter.case_number})</span>
              )}
              <button
                type="button"
                onClick={() => setSelectedMatter(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  color: 'var(--text-muted)',
                  padding: '0 0.15rem',
                  lineHeight: 1,
                }}
                title="Remove"
              >
                &times;
              </button>
            </div>
          ) : (
            <>
              <div style={{ position: 'relative' }}>
                <input
                  style={inputStyle}
                  value={matterSearch}
                  onChange={(e) => {
                    setMatterSearch(e.target.value);
                    setShowMatterDropdown(true);
                  }}
                  onFocus={() => { if (matterSearch.length >= 2) setShowMatterDropdown(true); }}
                  placeholder="Search by matter title, case number, or client..."
                />
                <span
                  style={{
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--text-muted)',
                    fontSize: '0.85rem',
                    pointerEvents: 'none',
                  }}
                >
                  <Search size={14} />
                </span>
              </div>
              {showMatterDropdown && debouncedMatterSearch.length >= 2 && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: 'var(--card)',
                    border: '1px solid var(--border)',
                    borderRadius: 6,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    zIndex: 10,
                    maxHeight: 200,
                    overflow: 'auto',
                  }}
                >
                  {matterResults.length === 0 ? (
                    <div style={{ padding: '0.6rem 0.75rem', color: 'var(--text-muted)', fontSize: '0.82rem' }}>
                      No matters found
                    </div>
                  ) : (
                    matterResults.map((m) => (
                      <div
                        key={m.id}
                        onClick={() => {
                          setSelectedMatter({ id: m.id, title: m.title, contact_name: m.contact_name, case_number: m.case_number });
                          setMatterSearch('');
                          setShowMatterDropdown(false);
                        }}
                        style={{
                          padding: '0.5rem 0.75rem',
                          cursor: 'pointer',
                          fontSize: '0.82rem',
                          borderBottom: '1px solid var(--border-light)',
                        }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'var(--primary-light)'; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = '#fff'; }}
                      >
                        <div style={{ fontWeight: 500 }}>{m.title}</div>
                        <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                          {m.contact_name}{m.case_number ? ` · ${m.case_number}` : ''}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Duplicate contact warning — fixed-height container prevents layout jumping */}
        <div style={{ minHeight: (debouncedFirstName.trim().length >= 2 && debouncedLastName.trim().length >= 2 && !dupDismissed) ? 44 : 0, transition: 'min-height 0.15s ease' }}>
          {!dupDismissed && (duplicates.length > 0 || dupLoading) && (
            <DuplicateContactWarning
              matches={duplicates}
              loading={dupLoading}
              onDismiss={() => setDupDismissed(true)}
            />
          )}
        </div>

        {mutation.isError && (
          <p style={{ color: '#c0392b', fontSize: '0.85rem', margin: '0.75rem 0 0' }}>
            {(mutation.error as { response?: { data?: { detail?: string } } })?.response?.data?.detail
              || 'Error creating contact. Please try again.'}
          </p>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.25rem' }}>
          <button
            onClick={onClose}
            style={{
              padding: '0.5rem 1rem',
              background: 'var(--bg)',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: '0.85rem',
            }}
          >
            Cancel
          </button>
          <button
            onClick={() => {
              setAttempted(true);
              if (canSubmit) mutation.mutate();
            }}
            disabled={mutation.isPending}
            style={{
              padding: '0.5rem 1rem',
              background: 'var(--primary)',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: '0.85rem',
              opacity: (!canSubmit || mutation.isPending) ? 0.5 : 1,
            }}
          >
            {mutation.isPending ? 'Creating...' : 'Create Contact'}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
